import React, { useEffect, useState, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle, Loader, Heart } from 'lucide-react';
import { toast } from 'sonner';
import './LegacyOnboardingPage.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EMPTY_ADDRESS = { street: '', city: '', state: '', postcode: '', country: '' };

function LegacyOnboardingPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [order, setOrder] = useState(null);
  const [error, setError] = useState(null);
  const [submitted, setSubmitted] = useState(false);

  const [form, setForm] = useState({
    customer_full_name: '',
    customer_address: { ...EMPTY_ADDRESS },
    loved_one_full_name: '',
    loved_one_preferred_name: '',
    loved_one_phone: '',
    loved_one_address: { ...EMPTY_ADDRESS },
    relationship: '',
    notes: '',
  });
  // alreadyDone = onboarding was already complete when this page first loaded.
  // (Distinct from `submitted`, which also flips to true after a fresh save in this session.)
  const [alreadyDone, setAlreadyDone] = useState(false);
  const [editing, setEditing] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/legacy/onboarding/${token}`);
      const data = res.data;
      setOrder(data);
      setForm({
        customer_full_name: data.customer_full_name || '',
        customer_address: { ...EMPTY_ADDRESS, ...(data.customer_address || {}) },
        loved_one_full_name: data.loved_one_full_name || '',
        loved_one_preferred_name: data.loved_one_preferred_name || '',
        loved_one_phone: data.loved_one_phone || '',
        loved_one_address: { ...EMPTY_ADDRESS, ...(data.loved_one_address || {}) },
        relationship: data.relationship || '',
        notes: data.notes || '',
      });
      setSubmitted(data.onboarding_complete);
      setAlreadyDone(data.onboarding_complete);
    } catch (e) {
      setError(e.response?.status === 404
        ? 'This onboarding link is no longer valid. Please contact admin@bindingthread.com.au.'
        : 'Something went wrong loading your onboarding form.');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const setField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));
  const setAddress = (which, key, value) => setForm((prev) => ({ ...prev, [which]: { ...prev[which], [key]: value } }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.customer_full_name.trim() || !form.customer_address.street.trim() ||
        !form.loved_one_full_name.trim() || !form.loved_one_address.street.trim()) {
      toast.error('Please fill in your name and both addresses.');
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/legacy/onboarding/${token}`, form);
      setSubmitted(true);
      toast.success('Thank you. Your details are saved.');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="legacy-onb-wrap">
        <div className="legacy-onb-loading">
          <Loader size={32} className="spin" />
          <p>Loading your onboarding form&hellip;</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="legacy-onb-wrap">
        <div className="legacy-onb-card">
          <h1>We couldn&apos;t find your order</h1>
          <p>{error}</p>
          <Link to="/" className="btn btn-secondary">Return home</Link>
        </div>
      </div>
    );
  }

  // Show a celebration / summary view if onboarding is already complete
  // and the customer hasn't asked to edit it.
  if (alreadyDone && !editing) {
    return (
      <div className="legacy-onb-wrap" data-testid="legacy-onboarding-page">
        <div className="legacy-onb-card">
          <div className="legacy-onb-already" data-testid="legacy-onboarding-already-done">
            <div className="legacy-onb-already-icon">
              <CheckCircle size={48} />
            </div>
            <span className="legacy-onb-eyebrow">{order?.collection_name || 'Your collection'}</span>
            <h1>You&apos;ve already shared their details.</h1>
            <p className="legacy-onb-sub">
              Thank you, {form.customer_full_name.split(' ')[0] || 'friend'}. We have everything we need to start preserving
              {form.loved_one_preferred_name ? <> <strong>{form.loved_one_preferred_name}</strong>&apos;s</> : <> your loved one&apos;s</>} stories.
              We&apos;ll be in touch when your first letter is ready.
            </p>

            <div className="legacy-onb-summary">
              <SummaryRow label="Loved one" value={form.loved_one_full_name + (form.loved_one_preferred_name ? ` (addressed as "${form.loved_one_preferred_name}")` : '')} />
              <SummaryRow label="Relationship" value={form.relationship} />
              <SummaryRow label="Their phone" value={form.loved_one_phone} />
              <SummaryRow label="Their address" value={[form.loved_one_address.street, form.loved_one_address.city, form.loved_one_address.state, form.loved_one_address.postcode, form.loved_one_address.country].filter(Boolean).join(', ')} />
              <SummaryRow label="Your mailing address" value={[form.customer_address.street, form.customer_address.city, form.customer_address.state, form.customer_address.postcode, form.customer_address.country].filter(Boolean).join(', ')} />
            </div>

            <div className="legacy-onb-actions">
              <button type="button" className="btn btn-secondary" onClick={() => setEditing(true)} data-testid="legacy-onboarding-edit-btn">
                Update details
              </button>
              <button type="button" className="btn btn-primary" onClick={() => navigate('/')}>
                Return to Binding Thread
              </button>
            </div>

            <p className="legacy-onb-footnote">
              If anything is incorrect or changes, just click <em>Update details</em> above, or write to <a href="mailto:admin@bindingthread.com.au">admin@bindingthread.com.au</a> and we&apos;ll fix it for you.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="legacy-onb-wrap" data-testid="legacy-onboarding-page">
      <div className="legacy-onb-card">
        <div className="legacy-onb-head">
          <span className="legacy-onb-eyebrow">{order?.collection_name || 'Your collection'}</span>
          <h1>{editing ? 'Update your details' : 'Tell us about your loved one'}</h1>
          <p className="legacy-onb-sub">
            A few quiet details so we know how to address every letter and where to post your keepsake tin.
            {' '}This page is private to you and you can return to it any time using the link in your welcome email.
          </p>
        </div>

        {submitted && (
          <div className="legacy-onb-success" data-testid="legacy-onboarding-saved">
            <CheckCircle size={22} />
            <p>Your details are saved. We&apos;ll be in touch when your first letter is ready.</p>
          </div>
        )}

        <form onSubmit={onSubmit} className="legacy-onb-form">

          <section className="legacy-onb-section">
            <h2><Heart size={16} /> About you</h2>
            <div className="legacy-onb-row">
              <label className="legacy-onb-field legacy-onb-field-full">
                <span>Your full name</span>
                <input data-testid="onb-customer-name" value={form.customer_full_name} onChange={(e) => setField('customer_full_name', e.target.value)} required />
              </label>
            </div>
            <AddressInputs prefix="onb-customer-addr" label="Your mailing address (where we&rsquo;ll post your keepsake tin and letters)" value={form.customer_address} onChange={(k, v) => setAddress('customer_address', k, v)} />
          </section>

          <section className="legacy-onb-section">
            <h2><Heart size={16} /> About your loved one</h2>
            <div className="legacy-onb-row">
              <label className="legacy-onb-field">
                <span>Full name</span>
                <input data-testid="onb-loved-name" value={form.loved_one_full_name} onChange={(e) => setField('loved_one_full_name', e.target.value)} required />
              </label>
              <label className="legacy-onb-field">
                <span>Preferred name <em>(what each letter will say)</em></span>
                <input data-testid="onb-loved-preferred" placeholder="e.g. Mum, Nan, Margaret" value={form.loved_one_preferred_name} onChange={(e) => setField('loved_one_preferred_name', e.target.value)} />
              </label>
              <label className="legacy-onb-field">
                <span>Relationship to you</span>
                <input data-testid="onb-relationship" placeholder="e.g. Mum, Dad, Grandfather" value={form.relationship} onChange={(e) => setField('relationship', e.target.value)} />
              </label>
              <label className="legacy-onb-field">
                <span>Their phone number</span>
                <input data-testid="onb-loved-phone" type="tel" placeholder="So we recognise them when they call" value={form.loved_one_phone} onChange={(e) => setField('loved_one_phone', e.target.value)} />
              </label>
            </div>
            <AddressInputs prefix="onb-loved-addr" label="Their physical address (we&rsquo;ll post them a small hello card with the free call number)" value={form.loved_one_address} onChange={(k, v) => setAddress('loved_one_address', k, v)} />
          </section>

          <section className="legacy-onb-section">
            <h2>Anything we should know? <span className="legacy-onb-optional">Optional</span></h2>
            <textarea
              data-testid="onb-notes"
              rows={5}
              placeholder="Stories you&rsquo;d love them to share, things they care about, the way they like to be addressed&hellip;"
              value={form.notes}
              onChange={(e) => setField('notes', e.target.value)}
            />
          </section>

          <div className="legacy-onb-actions">
            <button type="submit" className="btn btn-primary" disabled={saving} data-testid="onb-submit">
              {saving ? 'Saving…' : (submitted ? 'Update details' : 'Save details')}
            </button>
            <button type="button" className="btn btn-secondary" onClick={() => navigate('/')}>
              Return to Binding Thread
            </button>
          </div>
          <p className="legacy-onb-footnote">
            Your information stays private to you and Binding Thread. If you ever need help, simply reply to your welcome email or write to <a href="mailto:admin@bindingthread.com.au">admin@bindingthread.com.au</a>.
          </p>
        </form>
      </div>
    </div>
  );
}

function AddressInputs({ prefix, label, value, onChange }) {
  return (
    <div className="legacy-onb-address">
      <p className="legacy-onb-address-label" dangerouslySetInnerHTML={{ __html: label }} />
      <div className="legacy-onb-row">
        <label className="legacy-onb-field legacy-onb-field-full">
          <span>Street address</span>
          <input data-testid={`${prefix}-street`} value={value.street || ''} onChange={(e) => onChange('street', e.target.value)} required />
        </label>
        <label className="legacy-onb-field">
          <span>Suburb / City</span>
          <input data-testid={`${prefix}-city`} value={value.city || ''} onChange={(e) => onChange('city', e.target.value)} />
        </label>
        <label className="legacy-onb-field legacy-onb-field-sm">
          <span>State</span>
          <input data-testid={`${prefix}-state`} value={value.state || ''} onChange={(e) => onChange('state', e.target.value)} />
        </label>
        <label className="legacy-onb-field legacy-onb-field-sm">
          <span>Postcode</span>
          <input data-testid={`${prefix}-postcode`} value={value.postcode || ''} onChange={(e) => onChange('postcode', e.target.value)} />
        </label>
        <label className="legacy-onb-field">
          <span>Country</span>
          <input data-testid={`${prefix}-country`} placeholder="Australia / United States" value={value.country || ''} onChange={(e) => onChange('country', e.target.value)} />
        </label>
      </div>
    </div>
  );
}

function SummaryRow({ label, value }) {
  if (!value) return null;
  return (
    <div className="legacy-onb-summary-row">
      <span className="legacy-onb-summary-label">{label}</span>
      <span className="legacy-onb-summary-value">{value}</span>
    </div>
  );
}

export default LegacyOnboardingPage;
